import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @typedef {Object} SkioProductVariant
 * @property {string} id
 * @property {string} title
 * @property {number} price
 * @property {string} [image]
 * @property {boolean} available
 */

/**
 * @typedef {Object} SkioProduct
 * @property {string} id
 * @property {string} title
 * @property {SkioProductVariant[]} variants
 * @property {string} [image]
 */

/**
 * @typedef {Object} SkioDynamicBoxResponse
 * @property {boolean} success
 * @property {Object} data
 * @property {string} data.boxId
 * @property {number} data.minItems
 * @property {number} data.maxItems
 * @property {SkioProduct[]} data.products
 */

/**
 * @typedef {Object} SelectedItem
 * @property {string} productId
 * @property {string} productTitle
 * @property {string} variantId
 * @property {string} variantTitle
 * @property {number} quantity
 * @property {number} price
 * @property {string} [image]
 */

/**
 * A custom element that manages Skio Build a Box functionality.
 *
 * @typedef {object} SkioBuildABoxRefs
 * @property {HTMLElement} content - The content container.
 * @property {HTMLElement} loadingState - The loading state element.
 * @property {HTMLElement} errorState - The error state element.
 * @property {HTMLElement} errorMessage - The error message element.
 * @property {HTMLElement} productsContainer - The products container.
 * @property {HTMLElement} instructions - The instructions text.
 * @property {HTMLElement} productsGrid - The products grid container.
 * @property {HTMLElement} bundlePricing - The bundle pricing progress bar.
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 *
 * @extends Component<SkioBuildABoxRefs>
 */
class SkioBuildABoxComponent extends Component {
  requiredRefs = ['content', 'loadingState', 'errorState', 'errorMessage', 'productsContainer', 'instructions', 'productsGrid', 'bundlePricing', 'summary', 'summarySavings', 'summaryBadge', 'addToCartButton'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {SkioDynamicBoxResponse | null} */
  #boxData = null;

  /** @type {Map<string, SelectedItem>} */
  #selectedItems = new Map();

  /** @type {Map<string, any>} */
  #variantCatalog = new Map();

  /** @type {string | null} */
  #boxId = null;

  /** @type {any} */
  #discountTiers = null;

  /** @type {number} */
  #basePrice = 0;

  connectedCallback() {
    super.connectedCallback();
    // Automatically fetch and render products when component loads
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Initializes the component by fetching dynamic box data.
   */
  async #initialize() {
    const blockElement = /** @type {HTMLElement | null} */ (this.closest('.skio-build-a-box'));
    if (!blockElement) {
      this.#showError('Build a Box container not found.');
      return;
    }

    const productPlatformId = blockElement.dataset.productPlatformId;
    if (!productPlatformId || productPlatformId === 'null' || productPlatformId === '') {
      console.error('Product Platform ID missing:', {
        productPlatformId,
        productId: blockElement.dataset.productId,
        allData: blockElement.dataset
      });
      this.#showError('Product Platform ID is required. Please ensure the product has a variant selected.');
      return;
    }

    console.log('Fetching Skio box data for product platform ID:', productPlatformId);
    await this.#fetchDynamicBoxData(productPlatformId);
  };

  /**
   * Fetches dynamic box data from Skio API.
   * @param {string} productPlatformId - The product platform ID.
   */
  async #fetchDynamicBoxData(productPlatformId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#showLoading();
    this.#hideError();
    this.#hideProducts();

    const blockElement = /** @type {HTMLElement | null} */ (this.closest('.skio-build-a-box'));
    const apiEndpoint =
      blockElement?.dataset.skioApiEndpoint ||
      'https://api.skio.com/storefront-http/get-dynamic-box';

    try {
      const requestBody = {
        productPlatformId: productPlatformId,
      };
      
      console.log('Fetching from Skio API:', {
        endpoint: apiEndpoint,
        body: requestBody
      });

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Skio API error response:', errorText);
        throw new Error(`Failed to fetch dynamic box data: HTTP ${response.status} - ${errorText}`);
      }

      const data = /** @type {SkioDynamicBoxResponse | any} */ (await response.json());
      
      // Log the response for debugging
      console.log('Skio API response:', data);

      // Handle Skio API response structure: { ok: true, DynamicBox: { boxId, selectableProductVariants, ... } }
      let boxData = null;
      let boxId = null;

      // Check for Skio's actual response structure
      if (data.ok && data.DynamicBox) {
        // Skio API structure: { ok: true, DynamicBox: {...} }
        boxData = data.DynamicBox;
        boxId = data.DynamicBox.boxId;
        
        // Transform selectableProductVariants into individual variant cards
        // Each variant becomes its own "product" card for the list view
        if (boxData.selectableProductVariants && Array.isArray(boxData.selectableProductVariants)) {
          /** @type {any[]} */
          const variantCards = [];
          
          boxData.selectableProductVariants.forEach((/** @type {any} */ productData) => {
            const productTitle = productData.productTitle || 'Product';
            const productImage = productData.productImageSrc || null;
            
            // Transform each variant into its own card
            (productData.productVariants || []).forEach((/** @type {any} */ variantData) => {
              // Extract variant platform ID (Shopify GID format)
              const variantPlatformId = variantData.platformId?.split('/').pop() || 
                                      variantData.id || 
                                      variantData.variantId;
              
              // Price might be in cents or dollars - check if it's a reasonable dollar amount
              let price = variantData.price || 0;
              // If price is less than 100, assume it's in dollars and convert to cents
              if (price > 0 && price < 100) {
                price = Math.round(price * 100);
              }
              
              // Each variant becomes a standalone "product" card
              variantCards.push({
                id: variantPlatformId, // Use variant ID as the unique identifier
                variantId: variantPlatformId,
                platformId: variantData.platformId, // Keep original for cart operations
                title: variantData.productVariantTitle || variantData.title || 'Default Title',
                description: variantData.description || productData.description || '',
                price: price,
                compareAtPrice: variantData.compareAtPrice ? 
                  (variantData.compareAtPrice < 100 ? Math.round(variantData.compareAtPrice * 100) : variantData.compareAtPrice) : 
                  null,
                available: variantData.available !== false && variantData.available !== 'false',
                image: variantData.imageSrc || productImage,
                // Store product info for reference
                productId: productData.productPlatformId?.split('/').pop(),
                productTitle: productTitle
              });
            });
          });
          
          // Set products to be the flattened variant cards
          boxData.products = variantCards;
          console.log('Transformed variants into cards:', boxData.products);
        }
        
        // Extract min/max items - use direct values if available, otherwise calculate from sizeInterval
        if (boxData.minItems !== undefined && boxData.maxItems !== undefined) {
          // Already set from response
        } else if (boxData.sizeInterval && Array.isArray(boxData.sizeInterval) && boxData.sizeInterval.length > 0) {
          boxData.minItems = Math.min(...boxData.sizeInterval);
          boxData.maxItems = Math.max(...boxData.sizeInterval);
        }

        // Store discount tiers and base price for pricing calculations
        this.#discountTiers = boxData.fixedDiscountTier || boxData.percentDiscountTier || null;
        
        // Get base price from first variant (assuming all variants have similar pricing)
        if (boxData.products && boxData.products.length > 0) {
          this.#basePrice = boxData.products[0].price || 0;
        }
      } else if (data.success && data.data) {
        // Standard response structure: { success: true, data: { boxId, products, ... } }
        boxData = data.data;
        boxId = data.data.boxId || data.data.id;
      } else if (data.data && (data.data.products || data.data.boxId)) {
        // Response has data but no success flag
        boxData = data.data;
        boxId = data.data.boxId || data.data.id;
      } else if (data.products || data.boxId) {
        // Data is at root level: { boxId, products, minItems, maxItems, ... }
        boxData = data;
        boxId = data.boxId || data.id;
      } else if (data.dynamicBox) {
        // Nested under dynamicBox key (lowercase)
        boxData = data.dynamicBox;
        boxId = data.dynamicBox.boxId || data.dynamicBox.id;
      } else if (data.error || data.message) {
        // Error response
        throw new Error(data.message || data.error || 'Failed to load box configuration');
      } else {
        // Unknown structure - log everything for debugging
        console.error('Unexpected Skio API response structure. Full response:', data);
        console.error('Response keys:', Object.keys(data));
        throw new Error('Unexpected response structure from Skio API. Check console for details.');
      }

      // Validate we have the required data
      if (!boxData) {
        throw new Error('Invalid box configuration: no data found in response');
      }

      // Ensure products array exists
      if (!boxData.products || !Array.isArray(boxData.products)) {
        // Try alternative property names
        boxData.products = boxData.items || boxData.selectableProducts || boxData.selectableProductVariants || [];
      }
      
      if (boxData.products.length === 0) {
        console.warn('No products found in box data:', boxData);
        // Don't throw error, just show empty state
      }

      this.#boxData = { success: true, data: boxData };
      this.#boxId = boxId;
      this.#renderProducts(boxData);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Skio Build a Box error:', error);
      this.#showError(error.message || 'Failed to load box configuration. Please try again.');
    }
  }

  /**
   * Renders the products grid.
   * @param {{ products?: SkioProduct[], minItems?: number, maxItems?: number }} boxData - The box data from Skio API.
   */
  #renderProducts(boxData) {
    this.#hideLoading();
    this.#hideError();
    this.#showProducts();

    const products = boxData.products || [];
    const minItems = boxData.minItems || 0;
    const maxItems = boxData.maxItems || 0;

    // Update instructions
    if (this.refs.instructions) {
      const minText = minItems > 0 ? `Select at least ${minItems} item${minItems > 1 ? 's' : ''}` : '';
      const maxText = maxItems > 0 ? `Select up to ${maxItems} item${maxItems > 1 ? 's' : ''}` : '';
      const instructionText = [minText, maxText].filter(Boolean).join('. ') || 'Select items for your box';
      this.refs.instructions.textContent = instructionText;
    }

    // Clear existing products
    if (this.refs.productsGrid) {
      this.refs.productsGrid.innerHTML = '';
    }

    // Render bundle pricing progress bar
    this.#renderBundlePricing(boxData);

    // Cache variant cards for later UI updates
    this.#variantCatalog = new Map();
    products.forEach((/** @type {any} */ variantCard) => {
      if (variantCard?.variantId) this.#variantCatalog.set(variantCard.variantId, variantCard);
    });

    // Render variant cards (each variant is now its own card)
    products.forEach((/** @type {any} */ variantCard) => {
      const card = this.#createVariantCard(variantCard);
      if (this.refs.productsGrid && card) {
        this.refs.productsGrid.appendChild(card);
      }
    });

    this.#updateAddToCartButton();
    this.#updateBundlePricing();
    this.#syncAllVariantCardsUI();
  }

  /**
   * Creates a variant card element (each variant is its own card).
   * @param {any} variantCard - The variant card data.
   * @returns {HTMLElement | null} The variant card element.
   */
  #createVariantCard(variantCard) {
    const card = document.createElement('div');
    card.className = 'skio-build-a-box__variant-card';
    card.dataset.variantId = variantCard.variantId;

    // Card content wrapper
    const cardContent = document.createElement('div');
    cardContent.className = 'skio-build-a-box__variant-card-content';

    // Image container
    if (variantCard.image) {
      const imageContainer = document.createElement('div');
      imageContainer.className = 'skio-build-a-box__variant-card-image';
      const img = document.createElement('img');
      img.src = variantCard.image;
      img.alt = variantCard.title;
      imageContainer.appendChild(img);
      cardContent.appendChild(imageContainer);
    }

    // Text content
    const textContent = document.createElement('div');
    textContent.className = 'skio-build-a-box__variant-card-text';

    // Title
    const title = document.createElement('h3');
    title.className = 'skio-build-a-box__variant-card-title';
    title.textContent = variantCard.title;
    textContent.appendChild(title);

    // Description
    if (variantCard.description) {
      const description = document.createElement('p');
      description.className = 'skio-build-a-box__variant-card-description';
      description.textContent = variantCard.description;
      textContent.appendChild(description);
    }

    cardContent.appendChild(textContent);

    // Actions wrapper (for badge + button/controls)
    const actionsWrapper = document.createElement('div');
    actionsWrapper.className = 'skio-build-a-box__variant-card-actions-wrapper';

    // Badge (if applicable)
    const badge = this.#getVariantBadge(variantCard);
    if (badge) {
      const badgeElement = document.createElement('span');
      badgeElement.className = `skio-build-a-box__variant-card-badge skio-build-a-box__variant-card-badge--${badge.type}`;
      badgeElement.textContent = badge.text;
      actionsWrapper.appendChild(badgeElement);
    }

    // Actions (Add OR quantity controls)
    const actions = document.createElement('div');
    actions.className = 'skio-build-a-box__variant-actions';

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'button skio-build-a-box__variant-card-add-button';
    addButton.textContent = 'Add +';
    addButton.disabled = !variantCard.available;
    addButton.dataset.role = 'add';
    addButton.addEventListener('click', () => {
      this.#setVariantQuantity(variantCard, 1);
    });

    const qtyControls = document.createElement('div');
    qtyControls.className = 'skio-build-a-box__qty-controls';
    qtyControls.dataset.role = 'qty-controls';

    const minusButton = document.createElement('button');
    minusButton.type = 'button';
    minusButton.className = 'skio-build-a-box__qty-btn';
    minusButton.textContent = '−';
    minusButton.dataset.role = 'minus';
    minusButton.addEventListener('click', () => {
      const currentQty = this.#selectedItems.get(variantCard.variantId)?.quantity || 0;
      this.#setVariantQuantity(variantCard, Math.max(0, currentQty - 1));
    });

    const qtyValue = document.createElement('span');
    qtyValue.className = 'skio-build-a-box__qty-value';
    qtyValue.textContent = '0';
    qtyValue.dataset.role = 'qty';

    const plusButton = document.createElement('button');
    plusButton.type = 'button';
    plusButton.className = 'skio-build-a-box__qty-btn';
    plusButton.textContent = '+';
    plusButton.dataset.role = 'plus';
    plusButton.addEventListener('click', () => {
      const currentQty = this.#selectedItems.get(variantCard.variantId)?.quantity || 0;
      this.#setVariantQuantity(variantCard, currentQty + 1);
    });

    qtyControls.appendChild(minusButton);
    qtyControls.appendChild(qtyValue);
    qtyControls.appendChild(plusButton);

    actions.appendChild(addButton);
    actions.appendChild(qtyControls);
    actionsWrapper.appendChild(actions);

    card.appendChild(cardContent);
    card.appendChild(actionsWrapper);

    // Initialize UI state (card isn't in DOM yet, so pass it directly)
    this.#syncVariantCardUI(variantCard.variantId, card);

    return card;
  }

  /**
   * Gets badge info for a variant (NEW, BEST SELLER, BACK IN STOCK).
   * @param {any} variantCard - The variant card data.
   * @returns {{type: string, text: string} | null} Badge info or null.
   */
  #getVariantBadge(variantCard) {
    // Check for badge in variant data
    if (variantCard.badge) {
      const badgeType = variantCard.badge.toLowerCase().replace(/\s+/g, '-');
      return { type: badgeType, text: variantCard.badge };
    }
    
    // Check for tags/metafields that might indicate badges
    if (variantCard.tags?.includes('new') || variantCard.isNew) {
      return { type: 'new', text: 'NEW' };
    }
    if (variantCard.tags?.includes('bestseller') || variantCard.isBestseller) {
      return { type: 'bestseller', text: 'BEST SELLER' };
    }
    if (variantCard.tags?.includes('back-in-stock') || variantCard.isBackInStock) {
      return { type: 'back-in-stock', text: 'BACK IN STOCK' };
    }
    
    return null;
  }

  /**
   * Sets the quantity for a variant (0 removes it).
   * @param {any} variantCard
   * @param {number} nextQuantity
   */
  #setVariantQuantity(variantCard, nextQuantity) {
    if (!variantCard.available) return;

    const variantId = variantCard.variantId;

    const { maxItems } = this.#boxData?.data || {};
    const totalQty = this.#getTotalSelectedQuantity();
    const currentQty = this.#selectedItems.get(variantId)?.quantity || 0;

    // Enforce maxItems based on total quantity (not unique variants)
    if (maxItems && nextQuantity > currentQty && totalQty >= maxItems) {
      this.#showError(`You can select a maximum of ${maxItems} item${maxItems > 1 ? 's' : ''}.`);
      return;
    }

    if (nextQuantity <= 0) {
      this.#selectedItems.delete(variantId);
    } else {
      this.#selectedItems.set(variantId, {
        productId: variantCard.productId || variantId,
        productTitle: variantCard.productTitle || variantCard.title,
        variantId: variantId,
        variantTitle: variantCard.title,
        quantity: nextQuantity,
        price: variantCard.price,
        image: variantCard.image,
      });
    }

    this.#syncVariantCardUI(variantId);
    this.#updateAddToCartButton();
    this.#updateBundlePricing();
    this.#syncAllVariantCardsUI(); // update plus-button disabled states when max reached
  }

  /** @returns {number} */
  #getTotalSelectedQuantity() {
    return Array.from(this.#selectedItems.values()).reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Sync a single variant row UI (Add+ vs qty controls).
   * @param {string} variantId
   */
  /**
   * @param {string} variantId
   * @param {HTMLElement | null | undefined} [cardOverride]
   */
  #syncVariantCardUI(variantId, cardOverride) {
    const card =
      cardOverride ||
      /** @type {HTMLElement | null} */ (this.refs.productsGrid?.querySelector(`[data-variant-id="${variantId}"]`));
    if (!card) return;

    const qty = this.#selectedItems.get(variantId)?.quantity || 0;
    const addButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-role="add"]'));
    const qtyControls = /** @type {HTMLElement | null} */ (card.querySelector('[data-role="qty-controls"]'));
    const qtyValue = /** @type {HTMLElement | null} */ (card.querySelector('[data-role="qty"]'));
    const plusButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-role="plus"]'));
    const minusButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-role="minus"]'));

    if (qtyValue) qtyValue.textContent = String(qty);

    if (qty > 0) {
      card.classList.add('skio-build-a-box__variant-card--selected');
      if (addButton) addButton.style.display = 'none';
      if (qtyControls) qtyControls.style.display = 'inline-flex';
    } else {
      card.classList.remove('skio-build-a-box__variant-card--selected');
      if (addButton) addButton.style.display = '';
      if (qtyControls) qtyControls.style.display = 'none';
    }

    // Disable +/- based on constraints
    const { maxItems } = this.#boxData?.data || {};
    const totalQty = this.#getTotalSelectedQuantity();
    if (plusButton) plusButton.disabled = !!maxItems && totalQty >= maxItems;
    if (minusButton) minusButton.disabled = qty <= 0;
  }

  /**
   * Sync all variant rows (used when maxItems changes plus-button states).
   */
  #syncAllVariantCardsUI() {
    for (const variantId of this.#variantCatalog.keys()) {
      this.#syncVariantCardUI(variantId);
    }
  }

  /**
   * Renders the bundle pricing progress bar.
   * @param {any} boxData - The box data.
   */
  #renderBundlePricing(boxData) {
    if (!this.refs.bundlePricing) return;

    const sizeInterval = boxData.sizeInterval || [1, 2, 3, 4];
    const tiers = sizeInterval.map((/** @type {number} */ qty) => ({
      quantity: qty,
      label: qty === Math.max(...sizeInterval) ? `${qty}+ bags` : `${qty} bag${qty > 1 ? 's' : ''}`,
    }));

    const container = document.createElement('div');
    container.className = 'skio-build-a-box__bundle-pricing-container';

    const title = document.createElement('h3');
    title.className = 'skio-build-a-box__bundle-pricing-title';
    title.textContent = 'Build your bundle:';
    container.appendChild(title);

    const progressBar = document.createElement('div');
    progressBar.className = 'skio-build-a-box__bundle-progress';

    const progressLine = document.createElement('div');
    progressLine.className = 'skio-build-a-box__bundle-progress-line';

    tiers.forEach((/** @type {{ quantity: number, label: string }} */ tier, /** @type {number} */ index) => {
      const tierMarker = document.createElement('div');
      tierMarker.className = 'skio-build-a-box__bundle-tier';
      tierMarker.dataset.quantity = tier.quantity.toString();
      tierMarker.dataset.tierIndex = index.toString();

      const markerCircle = document.createElement('div');
      markerCircle.className = 'skio-build-a-box__bundle-tier-marker';

      const tierContent = document.createElement('div');
      tierContent.className = 'skio-build-a-box__bundle-tier-content';

      const tierLabel = document.createElement('div');
      tierLabel.className = 'skio-build-a-box__bundle-tier-label';
      tierLabel.textContent = /** @type {any} */ (tier).label;

      const tierPrice = document.createElement('div');
      tierPrice.className = 'skio-build-a-box__bundle-tier-price';
      tierPrice.textContent = `${this.#formatPrice(this.#calculatePricePerItem(/** @type {any} */ (tier).quantity))}/ea`;

      tierContent.appendChild(tierLabel);
      tierContent.appendChild(tierPrice);

      // Add "BEST DEAL" badge to the last tier
      if (index === tiers.length - 1) {
        const bestDealBadge = document.createElement('div');
        bestDealBadge.className = 'skio-build-a-box__bundle-best-deal';
        bestDealBadge.textContent = 'BEST DEAL';
        tierMarker.appendChild(bestDealBadge);
      }

      tierMarker.appendChild(markerCircle);
      tierMarker.appendChild(tierContent);
      progressBar.appendChild(tierMarker);
    });

    progressBar.appendChild(progressLine);
    container.appendChild(progressBar);
    this.refs.bundlePricing.innerHTML = '';
    this.refs.bundlePricing.appendChild(container);
  }

  /**
   * Calculates the price per item based on quantity and discount tiers.
   * @param {number} quantity - The quantity of items.
   * @returns {number} The price per item in cents.
   */
  #calculatePricePerItem(quantity) {
    if (!this.#basePrice || !this.#discountTiers) {
      return this.#basePrice || 0;
    }

    // Find the applicable discount tier
    let discount = 0;
    const tierKeys = Object.keys(this.#discountTiers).map(Number).sort((a, b) => b - a);
    
    for (const tierQty of tierKeys) {
      if (quantity >= tierQty) {
        discount = parseFloat(this.#discountTiers[tierQty]) || 0;
        break;
      }
    }

    // Calculate price: base price minus discount (assuming discount is in dollars)
    // If discount is less than base price, assume it's in dollars and convert to cents
    let discountInCents = discount;
    if (discount > 0 && discount < this.#basePrice / 100) {
      discountInCents = Math.round(discount * 100);
    }

    return Math.max(0, this.#basePrice - discountInCents);
  }

  /**
   * Updates the bundle pricing progress bar based on selected items.
   */
  #updateBundlePricing() {
    if (!this.refs.bundlePricing) return;

    const selectedCount = this.#getTotalSelectedQuantity();

    const progressBar = this.refs.bundlePricing.querySelector('.skio-build-a-box__bundle-progress');
    if (!progressBar) return;

    const tiers = progressBar.querySelectorAll('.skio-build-a-box__bundle-tier');
    const progressLine = /** @type {HTMLElement | null} */ (progressBar.querySelector('.skio-build-a-box__bundle-progress-line'));
    
    if (!progressLine) return;

    // Find the current tier based on selected count
    let currentTierIndex = -1;
    tiers.forEach((tier, index) => {
      const tierElement = /** @type {HTMLElement} */ (tier);
      const tierQty = parseInt(tierElement.dataset.quantity || '0', 10);
      const isActive = selectedCount >= tierQty;
      
      if (isActive) {
        currentTierIndex = index;
        tierElement.classList.add('skio-build-a-box__bundle-tier--active');
      } else {
        tierElement.classList.remove('skio-build-a-box__bundle-tier--active');
      }
    });

    // Update progress line width
    if (currentTierIndex >= 0 && tiers.length > 1) {
      const progressPercent = (currentTierIndex / (tiers.length - 1)) * 100;
      progressLine.style.width = `${progressPercent}%`;
    } else {
      progressLine.style.width = '0%';
    }
  }

  /**
   * Updates the add to cart button state.
   */
  #updateAddToCartButton() {
    if (!this.refs.addToCartButton) return;

    const { minItems, maxItems } = this.#boxData?.data || {};
    const selectedCount = this.#getTotalSelectedQuantity();

    let isValid = true;
    let errorMessage = '';

    if (minItems && selectedCount < minItems) {
      isValid = false;
      errorMessage = `Please select at least ${minItems} item${minItems > 1 ? 's' : ''}.`;
    } else if (maxItems && selectedCount > maxItems) {
      isValid = false;
      errorMessage = `Please select no more than ${maxItems} item${maxItems > 1 ? 's' : ''}.`;
    }

    this.refs.addToCartButton.disabled = !isValid || selectedCount === 0;

    // Update button text based on minItems requirement
    if (minItems && selectedCount < minItems) {
      const blockElement = /** @type {HTMLElement | null} */ (this.closest('.skio-build-a-box'));
      const addToCartText = blockElement?.dataset.addToCartText || 'Add to Cart';
      this.refs.addToCartButton.textContent = `Select at least ${minItems} item${minItems > 1 ? 's' : ''}`;
    } else {
      const blockElement = /** @type {HTMLElement | null} */ (this.closest('.skio-build-a-box'));
      const addToCartText = blockElement?.dataset.addToCartText || 'Add to Cart';
      this.refs.addToCartButton.textContent = addToCartText;
    }

    if (errorMessage && this.refs.errorMessage) {
      this.refs.errorMessage.textContent = errorMessage;
      this.#showError(errorMessage);
    } else {
      this.#hideError();
    }

    this.#updateSummary();
  }

  /**
   * Updates the summary section (savings and gifts unlocked).
   */
  #updateSummary() {
    const summary = /** @type {HTMLElement | null} */ (this.refs.summary);
    const summarySavings = /** @type {HTMLElement | null} */ (this.refs.summarySavings);
    const summaryBadge = /** @type {HTMLElement | null} */ (this.refs.summaryBadge);
    
    if (!summary || !summarySavings || !summaryBadge) return;

    const selectedCount = this.#getTotalSelectedQuantity();
    const { maxItems } = this.#boxData?.data || {};

    if (selectedCount === 0) {
      summary.classList.add('hidden');
      return;
    }

    summary.classList.remove('hidden');

    // Calculate savings percentage (example: based on tier)
    const savingsPercent = this.#calculateSavingsPercent();
    if (savingsPercent > 0) {
      summarySavings.textContent = `You're saving ${savingsPercent}%`;
    } else {
      summarySavings.textContent = '';
    }

    // Update gifts unlocked badge
    if (maxItems) {
      summaryBadge.textContent = `GIFTS UNLOCKED: ${selectedCount}/${maxItems}`;
    } else {
      summaryBadge.textContent = '';
    }
  }

  /**
   * Calculates savings percentage based on selected items and discount tiers.
   * @returns {number} Savings percentage.
   */
  #calculateSavingsPercent() {
    if (!this.#basePrice || !this.#discountTiers) return 0;

    const selectedCount = this.#getTotalSelectedQuantity();
    if (selectedCount === 0) return 0;

    // Find the applicable discount tier
    let discount = 0;
    const tierKeys = Object.keys(this.#discountTiers).map(Number).sort((a, b) => b - a);
    
    for (const tierQty of tierKeys) {
      if (selectedCount >= tierQty) {
        discount = parseFloat(this.#discountTiers[tierQty]) || 0;
        break;
      }
    }

    // Calculate percentage (assuming discount is in dollars and basePrice is in cents)
    if (discount > 0 && this.#basePrice > 0) {
      const discountInCents = discount < this.#basePrice / 100 ? Math.round(discount * 100) : discount;
      const percent = Math.round((discountInCents / this.#basePrice) * 100);
      return percent;
    }

    return 0;
  }

  /**
   * Adds selected items to cart.
   */
  addToCart = async () => {
    if (!this.#boxId || this.#selectedItems.size === 0) return;

    const { minItems, maxItems } = this.#boxData?.data || {};
    const selectedCount = this.#getTotalSelectedQuantity();

    if (minItems && selectedCount < minItems) {
      this.#showError(`Please select at least ${minItems} item${minItems > 1 ? 's' : ''}.`);
      return;
    }

    if (maxItems && selectedCount > maxItems) {
      this.#showError(`Please select no more than ${maxItems} item${maxItems > 1 ? 's' : ''}.`);
      return;
    }

    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.textContent = 'Adding to Cart...';
    }

    try {
      // Add each selected item to cart with Skio custom properties
      const addPromises = Array.from(this.#selectedItems.values()).map((item, index) => {
        const formData = new FormData();
        formData.append('id', item.variantId);
        formData.append('quantity', item.quantity.toString());
        formData.append('properties[_dynamicBoxIndex]', index.toString());
        if (this.#boxId) {
          formData.append('properties[_dynamicBoxId]', this.#boxId);
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        }).then((response) => response.json());
      });

      await Promise.all(addPromises);

      // Dispatch cart update event
      this.dispatchEvent(
        new CartAddEvent(
          {},
          this.id || '',
          {
            source: 'skio-build-a-box-component',
            itemCount: selectedCount,
          }
        )
      );

      // Reset selection after successful add to cart
      this.#selectedItems.clear();
      this.#updateAddToCartButton();
      this.#updateBundlePricing();
      this.#syncAllVariantCardsUI();

      // Optionally redirect to cart or show success message
      // window.location.href = '/cart';
    } catch (error) {
      console.error('Failed to add items to cart:', error);
      this.#showError('Failed to add items to cart. Please try again.');
      this.dispatchEvent(
        new CartErrorEvent(
          this.id || '',
          'Failed to add items to cart',
          error.message || 'Unknown error',
          {}
        )
      );
    } finally {
      if (this.refs.addToCartButton) {
        this.refs.addToCartButton.disabled = false;
      const blockElement = /** @type {HTMLElement | null} */ (this.closest('.skio-build-a-box'));
      const addToCartText = blockElement?.dataset.addToCartText || 'Add to Cart';
        this.refs.addToCartButton.textContent = addToCartText;
      }
    }
  };

  /**
   * Formats a price value.
   * @param {number} price - The price in cents.
   * @returns {string} The formatted price.
   */
  #formatPrice(price) {
    // Assuming price is in cents, convert to dollars
    const dollars = price / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(dollars);
  }

  /**
   * Shows the loading state.
   */
  #showLoading() {
    if (this.refs.loadingState) {
      this.refs.loadingState.classList.remove('hidden');
    }
  }

  /**
   * Hides the loading state.
   */
  #hideLoading() {
    if (this.refs.loadingState) {
      this.refs.loadingState.classList.add('hidden');
    }
  }

  /**
   * Shows the error state.
   * @param {string} message - The error message to display.
   */
  #showError(message) {
    if (this.refs.errorState) {
      this.refs.errorState.classList.remove('hidden');
    }
    if (this.refs.errorMessage && message) {
      this.refs.errorMessage.textContent = message;
    }
  }

  /**
   * Hides the error state.
   */
  #hideError() {
    if (this.refs.errorState) {
      this.refs.errorState.classList.add('hidden');
    }
  }

  /**
   * Shows the products container.
   */
  #showProducts() {
    if (this.refs.productsContainer) {
      this.refs.productsContainer.classList.remove('hidden');
    }
  }

  /**
   * Hides the products container.
   */
  #hideProducts() {
    if (this.refs.productsContainer) {
      this.refs.productsContainer.classList.add('hidden');
    }
  }

  /**
   * Resets the component state.
   */
  #resetState() {
    this.#selectedItems.clear();
    this.#variantCatalog.clear();
    this.#boxData = null;
    this.#boxId = null;
    if (this.refs.productsGrid) {
      this.refs.productsGrid.innerHTML = '';
    }
    this.#hideError();
    this.#hideProducts();
    this.#showLoading();
  }
}

if (!customElements.get('skio-build-a-box-component')) {
  customElements.define('skio-build-a-box-component', SkioBuildABoxComponent);
}

